import YourEmail, { YourEmailState } from 'components/FinancialCheckup/Email/YourEmail';
import VerifyEmail from 'components/VerifyEmail';
import YourIncome from 'components/FinancialCheckup/Income/YourIncome';
import MethodAuth from 'components/FinancialCheckup/MethodAuth';
import BirthDate from 'components/LoanForm/BirthDate';
import YourAddress from 'components/LoanForm/YourAddress';
import YourSSN4 from 'components/LoanForm/YourSSN/YourSSN';
import YourEmployer from 'components/LoanForm/Employer/YourEmployer';
import AdditionalIncome from 'components/DebtConsolidation/AdditionalIncome/AdditionalIncome';
import VerifyPhoneNumber from 'components/VerifyPhoneNumber';
import ConfirmPrefillDetails from 'components/LoanForm/ConfirmPrefillDetails';
import { RoutePath } from 'enums/Routes';
import YourPhoneNumber, { YourPhoneNumberState } from 'components/FinancialCheckup/PhoneNumber/YourPhoneNumber';
import AnalyzingUpdatedOffer from 'components/FinancialCheckup/Analyzing/AnalizingUpdatedOffer';
import ConfirmYourCredentials from 'components/LoanForm/ConfirmYourCredentials';
import ConfirmReapplyDetails from 'components/LoanForm/ConfirmReapplyDetails';
import {
  VerifyEmailResult,
  VerifyCodeResult,
  VerifyPhoneNumberResult,
  ConfirmPrefillDetailsResult,
  YourIncomeResult,
  YourGoalResult,
  YourEmployerResult,
  YourSSN4Result,
  ApplyingResult,
} from 'enums/FlowNextResults';
import { RootState } from 'handlers';
import { CurrentFlow } from 'enums/CurrentFlow';
import { setYourIncomeVariant, YourIncomeVariant } from 'handlers/yourIncome';
import LoanGoalsOnboarding from 'components/LoanGoals';
import { OfferAvailableState } from 'components/DebtConsolidation/OfferAvailable';
import { YourNameState } from 'components/LoanForm/YourName/YourName';
import AICoachGoalsOnboarding from 'components/AICoach/Goals/Goals';
import AICoachMonthlySavings from 'components/AICoach/MonthlySavingsAmount';

import { CustomRouterType, NextFunction, RouterType } from './types';

export enum OnboardingType {
  LoanGoals = 'loan-goals',
  AICoachGoals = 'ai-coach',
}

const signUpNavigationInfo = { navigationInfo: { title: 'Sign Up' } };

const goToNextOrConfirmReapply = (
  navigate: (path: RoutePath, options?: { state?: unknown }) => void,
  reduxState: RootState,
  fallback: RoutePath,
) => {
  if (reduxState.reapplyData.returnToConfirmAfterEdit) {
    navigate(RoutePath.ConfirmReapplyDetails);
    return;
  }
  navigate(fallback);
};

export const getApplyStageRoutes = (
  yourNameRouter: CustomRouterType,
  analyzingComponent: CustomRouterType['component'],
  next: NextFunction,
  onboardingType = OnboardingType.LoanGoals,
  includeAdditionalIncomeStep = false,
): RouterType => {
  const isDefaultFlow = onboardingType === OnboardingType.LoanGoals;

  return {
    ...(isDefaultFlow
      ? {
          [RoutePath.Goals]: {
            component: LoanGoalsOnboarding,
            handleNext: ({ navigate }) => (result) => {
              switch (result) {
                case YourGoalResult.CardCrossSell:
                  navigate(RoutePath.YourIncome);
                  break;
                default:
                  navigate(RoutePath.YourName);
              }
            },
          },
        }
      : {
          [RoutePath.AICoachGoals]: {
            navigationInfo: { title: 'Your Goals' },
            component: AICoachGoalsOnboarding,
            handleNext: ({ navigate }) => () => {
              navigate(RoutePath.YourName);
            },
          },
          [RoutePath.AICoachMonthlySavings]: {
            navigationInfo: { showBackLink: true, title: 'Sign Up' },
            component: AICoachMonthlySavings,
            handleNext: ({ navigate }) => () => {
              navigate(RoutePath.YourName);
            },
          },
        }),
    [RoutePath.YourName]: {
      ...yourNameRouter,
      navigationInfo: {
        showBackLink: true,
        title: 'Sign Up',
        step: isDefaultFlow ? 1 : undefined,
        stepCount: isDefaultFlow ? 5 : undefined,
      },
    },
    [RoutePath.ConfirmYourCredentials]: {
      navigationInfo: {
        showBackLink: true,
        title: 'Your Professional Credentials',
        step: isDefaultFlow ? 1 : undefined,
        stepCount: isDefaultFlow ? 5 : undefined,
      },
      component: ConfirmYourCredentials,
      handleNext: ({ navigate }) => () => {
        navigate(RoutePath.YourPhoneNumber);
      },
    },
    [RoutePath.YourPhoneNumber]: {
      navigationInfo: {
        showBackLink: true,
        title: 'Sign Up',
        step: isDefaultFlow ? 2 : undefined,
        stepCount: isDefaultFlow ? 5 : undefined,
      },
      component: YourPhoneNumber,
      handleNext: ({ navigate, state, dispatch }) => (result) => {
        switch (result) {
          case VerifyPhoneNumberResult.CanReapply:
            navigate(RoutePath.ConfirmReapplyDetails);
            break;
          case VerifyPhoneNumberResult.PrefillWithEmail:
            if (state.applicationStep.currentFlow === CurrentFlow.Card) {
              dispatch(setYourIncomeVariant(YourIncomeVariant.CardWaitlist));
              navigate(RoutePath.YourIncome);
              break;
            }
            navigate(RoutePath.ConfirmPrefillDetails);
            break;
          case VerifyPhoneNumberResult.PrefillNeedsEmail:
            navigate(RoutePath.YourEmail);
            break;
          default:
            navigate(RoutePath.VerifyPhoneNumber);
            break;
        }
      },
    },
    [RoutePath.VerifyPhoneNumber]: {
      navigationInfo: {
        showBackLink: true,
        title: 'Sign Up',
        step: isDefaultFlow ? 3 : undefined,
        stepCount: isDefaultFlow ? 5 : undefined,
      },
      component: VerifyPhoneNumber,
      handleNext: ({ navigate, state, dispatch }) => async (result) => {
        switch (result) {
          case VerifyCodeResult.DifferentPhoneNumberSession:
            navigate(RoutePath.YourPhoneNumber, {
              state: { isDifferentPhoneNumberSession: true } as YourPhoneNumberState,
            });
            break;
          case VerifyPhoneNumberResult.CanReapply:
            navigate(RoutePath.ConfirmReapplyDetails);
            break;
          case VerifyPhoneNumberResult.PrefillWithEmail:
            if (state.applicationStep.currentFlow === CurrentFlow.Card) {
              dispatch(setYourIncomeVariant(YourIncomeVariant.CardWaitlist));
              navigate(RoutePath.YourIncome);
              break;
            }
            navigate(RoutePath.ConfirmPrefillDetails);
            break;
          case VerifyPhoneNumberResult.PrefillNeedsEmail:
            navigate(RoutePath.YourEmail);
            break;
          default:
            navigate(RoutePath.YourEmail);
            break;
        }
      },
    },
    [RoutePath.ConfirmPrefillDetails]: {
      ...signUpNavigationInfo,
      component: ConfirmPrefillDetails,
      handleNext: ({ navigate }) => (result) => {
        switch (result) {
          case ConfirmPrefillDetailsResult.UpdateFields:
            navigate(RoutePath.YourBirthDate);
            break;
          case ConfirmPrefillDetailsResult.SessionExpired:
            navigate(RoutePath.YourName, { state: { isSessionExpired: true } as YourNameState });
            break;
          case ConfirmPrefillDetailsResult.Error:
            navigate(RoutePath.Error);
            break;
          case ConfirmPrefillDetailsResult.Confirmed:
          default:
            navigate(RoutePath.YourEmployer);
            break;
        }
      },
    },
    [RoutePath.YourEmail]: {
      navigationInfo: {
        showBackLink: true,
        title: 'Sign Up',
        step: isDefaultFlow ? 4 : undefined,
        stepCount: isDefaultFlow ? 5 : undefined,
      },
      component: YourEmail,
      handleNext: ({ navigate }) => () => {
        navigate(RoutePath.VerifyEmail);
      },
    },
    [RoutePath.VerifyEmail]: {
      navigationInfo: {
        showBackLink: true,
        title: 'Sign Up',
        step: isDefaultFlow ? 4 : undefined,
        stepCount: isDefaultFlow ? 5 : undefined,
      },
      component: VerifyEmail,
      handleNext: ({ navigate, state }) => async (result) => {
        switch (result) {
          case VerifyCodeResult.DifferentEmailSession:
            navigate(RoutePath.YourEmail, { state: { isDifferentEmailSession: true } as YourEmailState });
            break;
          case VerifyEmailResult.CardFlow:
            goToNextOrConfirmReapply(navigate, state, RoutePath.YourIncome);
            break;
          default:
            if (state.auth?.prefill && !state.reapplyData.returnToConfirmAfterEdit) {
              navigate(RoutePath.ConfirmPrefillDetails);
            } else {
              goToNextOrConfirmReapply(navigate, state, RoutePath.YourBirthDate);
            }
            break;
        }
      },
    },
    [RoutePath.YourIncome]: {
      navigationInfo: {
        showBackLink: true,
        title: 'Sign Up',
        step: isDefaultFlow ? 5 : undefined,
        stepCount: isDefaultFlow ? 5 : undefined,
      },
      component: YourIncome,
      handleNext: ({ navigate, state }) => (result) => {
        switch (result) {
          case YourIncomeResult.CardWaitlist:
            navigate(RoutePath.BuildCreditAvoidDebtSaveMoney, {
              state: {
                fromApplyStage: true,
              },
            });
            break;
          case YourIncomeResult.CardCrossSell:
            goToNextOrConfirmReapply(navigate, state, RoutePath.YourBirthDate);
            break;
          default:
            goToNextOrConfirmReapply(navigate, state, RoutePath.Analyzing);
            break;
        }
      },
    },
    [RoutePath.MethodAuth]: {
      navigationInfo: {
        showBackLink: true,
        title: 'Connect',
      },
      component: MethodAuth,
      handleNext: () => () => {},
    },

    [RoutePath.YourBirthDate]: {
      ...signUpNavigationInfo,
      component: BirthDate,
      handleNext: ({ navigate, state }) => () => {
        goToNextOrConfirmReapply(navigate, state, RoutePath.YourAddress);
      },
    },
    [RoutePath.YourSSN4]: {
      ...signUpNavigationInfo,
      component: YourSSN4,
      handleNext: ({ navigate, state }) => (result) => {
        switch (result) {
          case YourSSN4Result.CardCrossSell:
            goToNextOrConfirmReapply(navigate, state, RoutePath.Analyzing);
            break;
          case YourSSN4Result.SessionExpired:
            navigate(RoutePath.YourName, { state: { isSessionExpired: true } as YourNameState });
            break;
          case YourSSN4Result.Error:
            navigate(RoutePath.Error);
            break;
          default:
            goToNextOrConfirmReapply(navigate, state, RoutePath.YourEmployer);
        }
      },
    },
    [RoutePath.YourEmployer]: {
      navigationInfo: {
        title: 'Sign Up',
        step: isDefaultFlow ? 5 : undefined,
        stepCount: isDefaultFlow ? 5 : undefined,
      },
      component: YourEmployer,
      handleNext: ({ navigate, state }) => (result) => {
        switch (result) {
          case YourEmployerResult.IncomeRequired:
            goToNextOrConfirmReapply(navigate, state, RoutePath.YourIncome);
            break;
          default:
            goToNextOrConfirmReapply(
              navigate,
              state,
              includeAdditionalIncomeStep ? RoutePath.AdditionalIncome : RoutePath.Analyzing,
            );
        }
      },
    },
    [RoutePath.AdditionalIncome]: {
      navigationInfo: {
        showBackLink: true,
        title: 'Sign Up',
        step: isDefaultFlow ? 5 : undefined,
        stepCount: isDefaultFlow ? 5 : undefined,
      },
      component: AdditionalIncome,
      handleNext: ({ navigate, state }) => () => {
        goToNextOrConfirmReapply(navigate, state, RoutePath.Analyzing);
      },
    },
    [RoutePath.YourAddress]: {
      ...signUpNavigationInfo,
      component: YourAddress,
      handleNext: ({ navigate, state }) => () => {
        goToNextOrConfirmReapply(navigate, state, RoutePath.YourSSN4);
      },
    },
    [RoutePath.ConfirmReapplyDetails]: {
      navigationInfo: {
        showBackLink: true,
        title: 'Sign Up',
      },
      component: ConfirmReapplyDetails,
      handleNext: ({ navigate }) => () => {
        navigate(RoutePath.Analyzing);
      },
    },
    [RoutePath.Analyzing]: {
      component: analyzingComponent,
      handleNext: next,
    },
    [RoutePath.AnalyzingUpdatedOffer]: {
      component: AnalyzingUpdatedOffer,
      handleNext: ({ navigate }) => (result) => {
        switch (result) {
          case ApplyingResult.Done:
            navigate(RoutePath.OfferAvailable, { state: { updateOffers: true } as OfferAvailableState });
            break;
          default:
            navigate(RoutePath.FinancialDashboard);
            break;
        }
      },
    },
  };
};
